import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  Alert,
  Checkbox,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';

import { Workspace } from '../../types';
import { generateWorkspaceName } from '../../utils/workspace-naming';
import { apiService } from '../../services/api';

interface SaveStateDialogProps {
  isOpen: boolean;
  tabTitle: string;
  workspace?: Workspace;
  existingName?: string;
  existingAutoOpen?: boolean; // Existing auto-open setting to preserve
  isManualSave?: boolean; // Skip confirmation for manual saves
  onConfirm: (name: string, autoOpenOnStartup?: boolean) => void;
  onCancel: () => void;
}

export const SaveStateDialog: React.FC<SaveStateDialogProps> = ({
  isOpen,
  tabTitle,
  workspace,
  existingName,
  existingAutoOpen,
  isManualSave = false,
  onConfirm,
  onCancel,
}) => {
  const [step, setStep] = useState<'confirm' | 'name'>('confirm');
  const [stateName, setStateName] = useState('');
  const [autoOpenOnStartup, setAutoOpenOnStartup] = useState(false);
  const [error, setError] = useState('');
  
  // Use the AI name from the workspace instance
  const aiName = workspace?.aiName || 'AI';

  useEffect(() => {
    const initializeDialog = async () => {
      if (isOpen) {
        // Skip confirmation for manual saves, go straight to name entry
        setStep(isManualSave ? 'name' : 'confirm');
        setError('');
        
        // Preserve existing auto-open setting or default to false for new saves
        setAutoOpenOnStartup(existingAutoOpen ?? false);
        
        // Set default name based on existing name or generate one
        if (existingName) {
          setStateName(existingName);
        } else if (workspace) {
          try {
            // Get all existing saved state names to generate unique name
            const savedStates = await apiService.getSavedStates();
            const existingNames = savedStates.map(state => state.name);
            
            // Generate new name using the improved format
            const newName = generateWorkspaceName(workspace.name, aiName, existingNames);
            setStateName(newName);
          } catch (error) {
            console.error('Failed to fetch existing saved states for naming:', error);
            // Fallback to old naming if API fails
            const timestamp = new Date().toLocaleString();
            setStateName(`${aiName}'s session - ${timestamp}`);
          }
        } else {
          // Fallback when no workspace is available
          const timestamp = new Date().toLocaleString();
          setStateName(`${aiName}'s session - ${timestamp}`);
        }
      }
    };

    initializeDialog();
  }, [isOpen, tabTitle, workspace, existingName, existingAutoOpen, isManualSave, aiName]);

  const handleConfirmSave = () => {
    setStep('name');
  };

  const handleSave = () => {
    if (!stateName.trim()) {
      setError('Please enter a name for the saved state');
      return;
    }
    
    setError('');
    onConfirm(stateName.trim(), autoOpenOnStartup);
  };

  const handleCancel = () => {
    setStep('confirm');
    setError('');
    onCancel();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStateName(event.target.value);
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && step === 'name') {
      handleSave();
    }
  };

  if (step === 'confirm') {
    return (
      <Modal
        open={isOpen}
        onCancel={handleCancel}
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
          }}>
            <SaveOutlined style={{ fontSize: '20px' }} />
            Save workspace state?
          </div>
        }
        okText="Save & Close"
        cancelText="Close without saving"
        onOk={handleConfirmSave}
        width={400}
      >
        <p style={{ marginBottom: '1rem' }}>
          You have unsaved changes in the <strong>"{tabTitle}"</strong> workspace tab.
        </p>
        
        <p style={{ 
          fontSize: '0.875rem',
          opacity: 0.8,
          margin: 0
        }}>
          Saving will preserve your current chat history and workspace state so you can restore it later.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      title={
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
        }}>
          <SaveOutlined style={{ fontSize: '20px' }} />
          Name your saved state
        </div>
      }
      okText="Save"
      cancelText="Cancel"
      onOk={handleSave}
      width={400}
    >
      {error && (
        <Alert
          type="error"
          message="Error"
          description={error}
          showIcon
          style={{ marginBottom: '1rem' }}
        />
      )}
      
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Saved state name</label>
        <Input
          id="state-name-input"
          placeholder="Enter a name for this saved state"
          value={stateName}
          onChange={handleNameChange}
          onKeyDown={handleKeyPress}
          autoFocus
        />
        <div style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: '4px' }}>
          This name will help you identify this saved state later
        </div>
      </div>
      
      <div style={{ marginTop: '1rem' }}>
        <Checkbox
          checked={autoOpenOnStartup}
          onChange={(e) => setAutoOpenOnStartup(e.target.checked)}
        >
          Auto-open on startup
        </Checkbox>
        <div style={{ 
          fontSize: '0.75rem',
          opacity: 0.65,
          marginTop: '0.25rem',
          marginLeft: '1.5rem'
        }}>
          Automatically restore this workspace when the app starts
        </div>
      </div>
    </Modal>
  );
};